import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'

const DashboardPage = () => {
  const { user } = useAuthStore()
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false)

  useEffect(() => {
    // Determine if this is a first-time user based on their journey usage
    if (user) {
      const journeysUsed = user.journeys_used || 0
      setIsFirstTimeUser(journeysUsed === 0)
    }
  }, [user])

  // Helper function to display limits
  const formatLimit = (limit) => {
    return limit === -1 ? 'Unlimited' : limit
  }

  // Helper function to display usage vs limit
  const formatUsageDisplay = (used, limit) => {
    if (limit === -1) {
      return `${used} used`
    }
    if (limit === undefined || limit === null) {
      return `${used} used`
    }
    return `${used} of ${limit}`
  }

  // Get user limits based on user type
  const getUserLimits = () => {
    const userType = user?.user_type || 'free'
    
    switch (userType) {
      case 'admin':
        return {
          max_journeys: -1,
          max_pdf_generations: -1,
          max_consultant_queries: -1
        }
      case 'premium':
        return {
          max_journeys: 10,
          max_pdf_generations: 50,
          max_consultant_queries: 100
        }
      case 'free':
      default:
        return {
          max_journeys: 3,
          max_pdf_generations: 5,
          max_consultant_queries: 20
        }
    }
  }

  const userLimits = getUserLimits()
  const maxJourneys = user?.max_journeys !== undefined ? user.max_journeys : userLimits.max_journeys
  const maxPdfGenerations = user?.max_pdf_generations !== undefined ? user.max_pdf_generations : userLimits.max_pdf_generations
  const maxConsultantQueries = user?.max_consultant_queries !== undefined ? user.max_consultant_queries : userLimits.max_consultant_queries

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {isFirstTimeUser ? 'Welcome' : 'Welcome back'}, {user?.full_name || user?.email?.split('@')[0] || 'User'}!
        </h1>
        <p className="text-lg text-gray-600">
          {user?.user_type === 'admin' ? 
            'You have full admin access to the Samvadic platform.' :
            isFirstTimeUser ?
              'Ready to discover the deeper truths that will anchor your brand?' :
              'Continue your brand strategy journey where you left off.'
          }
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Journeys Started</h3>
          <p className="text-3xl font-bold text-blue-600">{user?.journeys_used || 0}</p>
          <p className="text-sm text-gray-500 mt-1">
            {formatUsageDisplay(user?.journeys_used || 0, maxJourneys)}
          </p>
          {maxJourneys === -1 && (
            <span className="inline-block mt-2 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">
              Unlimited Access
            </span>
          )}
        </div>
        
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500 mb-2">PDFs Generated</h3>
          <p className="text-3xl font-bold text-green-600">{user?.pdfs_generated_this_month || 0}</p>
          <p className="text-sm text-gray-500 mt-1">
            {formatUsageDisplay(user?.pdfs_generated_this_month || 0, maxPdfGenerations)} this month
          </p>
          {maxPdfGenerations === -1 && (
            <span className="inline-block mt-2 px-2 py-1 text-xs font-medium text-green-600 bg-green-100 rounded-full">
              Unlimited Access
            </span>
          )}
        </div>
        
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Consultant Queries</h3>
          <p className="text-3xl font-bold text-purple-600">{user?.consultant_queries_this_month || 0}</p>
          <p className="text-sm text-gray-500 mt-1">
            {formatUsageDisplay(user?.consultant_queries_this_month || 0, maxConsultantQueries)} this month
          </p>
          {maxConsultantQueries === -1 && (
            <span className="inline-block mt-2 px-2 py-1 text-xs font-medium text-purple-600 bg-purple-100 rounded-full">
              Unlimited Access
            </span>
          )}
        </div>
      </div>

      {/* Admin Panel Link - Only show for admin users */}
      {user?.user_type === 'admin' && (
        <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span className="text-sm font-medium text-yellow-800">
              Admin Mode Active - <Link to="/admin" className="underline hover:text-yellow-900">Access Admin Panel</Link>
            </span>
          </div>
        </div>
      )}

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Start Journey */}
        <Link 
          to="/journey"
          className="group bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 hover:border-blue-300 transition-all duration-200 hover:shadow-md"
        >
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {isFirstTimeUser ? 'Start Brand Journey' : 'Continue Journey'}
          </h3>
          <p className="text-sm text-gray-600">
            {isFirstTimeUser ? 
              'Begin your discovery process with our AI-powered brand strategist.' :
              'Continue your brand strategy conversation where you left off.'
            }
          </p>
        </Link>

        {/* View Anchors */}
        <Link 
          to="/anchors"
          className="group bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200 hover:border-green-300 transition-all duration-200 hover:shadow-md"
        >
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Your Anchors</h3>
          <p className="text-sm text-gray-600">Review and refine your Four Strategic Anchors.</p>
        </Link>

        {/* Consultant Mode */}
        <Link 
          to="/consultant"
          className="group bg-gradient-to-br from-purple-50 to-violet-50 p-6 rounded-xl border border-purple-200 hover:border-purple-300 transition-all duration-200 hover:shadow-md"
        >
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Consultant</h3>
          <p className="text-sm text-gray-600">Get strategic advice and answers to your brand questions.</p>
        </Link>

        {/* Documents */}
        <Link 
          to="/documents"
          className="group bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl border border-orange-200 hover:border-orange-300 transition-all duration-200 hover:shadow-md"
        >
          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Documents</h3>
          <p className="text-sm text-gray-600">Access your generated brand strategy documents and PDFs.</p>
        </Link>
      </div>

      {/* Recent Activity */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="text-center py-8">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-gray-500">
              {isFirstTimeUser ? 
                'No recent activity yet. Start your brand journey to see updates here!' :
                'Your recent activity will appear here as you continue your brand strategy journey.'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage